import { forwardRef, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import Popper from '@material-ui/core/Popper';
import { isAppZoomBelow100 } from './fullCalendarZoom';

const getAnchorElement = (anchorEl) => {
  if (!anchorEl) return null;
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
};

const getAnchorVisualWidth = (anchorEl) => {
  const anchor = getAnchorElement(anchorEl);
  if (!anchor) return null;
  return Math.ceil(anchor.getBoundingClientRect().width);
};

const buildZoomPopperModifiers = () => {
  if (!isAppZoomBelow100()) return [];

  return [
    {
      name: 'flip',
      enabled: false
    },
    {
      name: 'computeStyles',
      options: {
        gpuAcceleration: false
      }
    },
    {
      name: 'appZoomAutocompleteWidth',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }) => {
        const referenceWidth = state.rects.reference.width;
        if (referenceWidth > 0) {
          const widthPx = `${Math.ceil(referenceWidth)}px`;
          state.styles.popper.width = widthPx;
          state.styles.popper.maxWidth = widthPx;
        }
        state.styles.popper.zoom = '';
      }
    }
  ];
};

const mergePopperOptions = (popperOptions) => ({
  ...popperOptions,
  strategy: 'fixed',
  modifiers: [
    ...(popperOptions?.modifiers || []),
    ...buildZoomPopperModifiers()
  ]
});

const applyAutocompletePopperWidth = (popperNode, anchorEl) => {
  if (!popperNode) return;

  if (!isAppZoomBelow100()) {
    popperNode.style.removeProperty('zoom');
    return;
  }

  popperNode.style.removeProperty('zoom');
  const visualWidth = getAnchorVisualWidth(anchorEl);
  if (visualWidth > 0) {
    const widthPx = `${visualWidth}px`;
    popperNode.style.width = widthPx;
    popperNode.style.maxWidth = widthPx;
  }
};

export const AutocompleteZoomPopper = forwardRef(function AutocompleteZoomPopper(props, ref) {
  const { style, anchorEl, open, popperOptions, ...other } = props;
  const popperNodeRef = useRef(null);
  const anchorElRef = useRef(anchorEl);

  anchorElRef.current = anchorEl;

  const setPopperRef = useCallback(
    (node) => {
      popperNodeRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  const adjustedStyle = useMemo(() => {
    if (!isAppZoomBelow100()) return style;

    const visualWidth = getAnchorVisualWidth(anchorEl);
    if (!visualWidth) return style;

    return {
      ...style,
      width: visualWidth
    };
  }, [anchorEl, style]);

  const mergedPopperOptions = useMemo(
    () => mergePopperOptions(popperOptions),
    [popperOptions]
  );

  const syncPopperWidth = useCallback(() => {
    if (!open) return;
    applyAutocompletePopperWidth(popperNodeRef.current, anchorElRef.current);
  }, [open]);

  useLayoutEffect(() => {
    syncPopperWidth();
    if (!open) return undefined;

    const frameId = requestAnimationFrame(() => {
      syncPopperWidth();
      requestAnimationFrame(syncPopperWidth);
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, syncPopperWidth, adjustedStyle?.width]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const handleLayoutChange = () => {
      syncPopperWidth();
    };

    window.addEventListener('app_zoom_update', handleLayoutChange);
    window.addEventListener('resize', handleLayoutChange);
    document.addEventListener('scroll', handleLayoutChange, true);

    return () => {
      window.removeEventListener('app_zoom_update', handleLayoutChange);
      window.removeEventListener('resize', handleLayoutChange);
      document.removeEventListener('scroll', handleLayoutChange, true);
    };
  }, [open, syncPopperWidth]);

  return (
    <Popper
      ref={setPopperRef}
      style={adjustedStyle}
      anchorEl={anchorEl}
      open={open}
      popperOptions={mergedPopperOptions}
      {...other}
    />
  );
});

const getOpenAutocompleteAnchors = () =>
  [...document.querySelectorAll('.MuiAutocomplete-root')].flatMap((root) => {
    const expandedInput = root.querySelector('[aria-expanded="true"]');
    if (!expandedInput) return [];

    const anchor = root.querySelector('.MuiAutocomplete-inputRoot');
    return anchor ? [anchor] : [];
  });

export const fixAutocompletePoppersForAppZoom = () => {
  const openAnchors = getOpenAutocompleteAnchors();
  const popperNodes = [...document.querySelectorAll('.MuiAutocomplete-popper')];

  popperNodes.forEach((popperNode, index) => {
    popperNode.style.removeProperty('zoom');

    if (!isAppZoomBelow100()) return;

    const anchor = openAnchors[index] || openAnchors[0];
    applyAutocompletePopperWidth(popperNode, anchor);
  });
};

let frameId = null;
let burstTimeoutIds = [];

const clearBurstTimeouts = () => {
  burstTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
  burstTimeoutIds = [];
};

export const scheduleAutocompleteZoomFix = ({ burst = false } = {}) => {
  if (frameId) cancelAnimationFrame(frameId);
  frameId = requestAnimationFrame(() => {
    fixAutocompletePoppersForAppZoom();
    frameId = requestAnimationFrame(() => {
      fixAutocompletePoppersForAppZoom();
      frameId = null;
    });
  });

  if (!burst) return;

  clearBurstTimeouts();
  [0, 50, 150, 350].forEach((delayMs) => {
    burstTimeoutIds.push(setTimeout(fixAutocompletePoppersForAppZoom, delayMs));
  });
};

export const subscribeGlobalAutocompleteZoomFix = () => {
  const handleLayoutChange = () => {
    scheduleAutocompleteZoomFix();
  };

  const mutationObserver = new MutationObserver((mutations) => {
    const hasAutocompletePopperChange = mutations.some((mutation) => {
      if (mutation.type === 'childList') {
        return [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node instanceof Element
            && (node.matches?.('.MuiAutocomplete-popper')
              || node.querySelector?.('.MuiAutocomplete-popper'))
        );
      }

      const { target } = mutation;
      return target instanceof Element
        && (target.classList?.contains('MuiAutocomplete-popper')
          || target.classList?.contains('MuiAutocomplete-inputRoot'));
    });

    if (hasAutocompletePopperChange) {
      scheduleAutocompleteZoomFix({ burst: true });
    }
  });

  mutationObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });

  window.addEventListener('app_zoom_update', handleLayoutChange);
  window.addEventListener('resize', handleLayoutChange);
  scheduleAutocompleteZoomFix();

  return () => {
    if (frameId) cancelAnimationFrame(frameId);
    clearBurstTimeouts();
    mutationObserver.disconnect();
    window.removeEventListener('app_zoom_update', handleLayoutChange);
    window.removeEventListener('resize', handleLayoutChange);
  };
};
